import { apiPost } from '@/features/shared/utils/dataFetching'
import {
  type Content,
  type CMSCatNavData,
  type CMSPageData,
  type LayoutGroupData,
  type AlcoholComplianceTerms,
  type AdShowcaseData,
  type StandardNavData,
  type CMSProductShelfData,
  type AdVideoShowcaseData,
} from '@/features/cms/components/CMS/types'
import { type QueryFunctionContext } from '@tanstack/react-query'
import { isOnServer } from '@shared/constants/util'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import {
  type getAddressParamsForCMS,
  createFetchCMSDataConfig,
  toCategory,
} from '@/features/cms/services/CMS/utils'
import { type CMSInputVariables } from '@/features/cms/services/CMS/queryOptions'
import { type ShoppingCategories } from '@/features/shop/services/ShoppingCategories/types'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { type ParsedUrlQuery } from 'node:querystring'

type CMSQueryKey<T> = Readonly<
  [
    `CMS_${string}`,
    {
      slug?: string
      storeParams: StoreParams | undefined
      addressParams: ReturnType<typeof getAddressParamsForCMS> | undefined
      subscriptionType: string | undefined
      hasPlacedMarketplaceOrder: boolean | undefined
      inputVariables?: CMSInputVariables
      utmParams?: ParsedUrlQuery
    } & T,
  ]
>

export type CMSDataQueryFunctionContext<T extends object = object> = QueryFunctionContext<
  CMSQueryKey<T>
>

export const fetchCMSData = (
  context: CMSDataQueryFunctionContext<{ enableDeferredHydration: boolean }>,
) => {
  const [, { enableDeferredHydration }] = context.queryKey
  return apiPost<CMSPageData>({
    config: {
      ...createFetchCMSDataConfig({
        context,
        isOnServer: isOnServer(),
        enableDeferredHydration,
      }),
      signal: context.signal,
    },
    context,
    fetcherName: 'fetchCMSData',
  })
}

export const fetchDeferredHydration = ({
  url,
  headers,
  body,
}: {
  url: string
  headers: Record<string, string>
  body: unknown
}) => {
  // url from hydration callback object does not include gateway path
  const fullUrl = `/cms${url}`
  return apiPost<Content>({
    fetcherName: 'fetchDeferredHydration',
    config: {
      url: fullUrl,
      headers,
      data: body,
    },
  })
}

export const fetchCMSCatNavData = async (
  context: CMSDataQueryFunctionContext,
): Promise<ShoppingCategories> => {
  const response = await apiPost<CMSCatNavData>({
    config: createFetchCMSDataConfig({
      context,
      isOnServer: isOnServer(),
      contentAlias: 'web-category-nav',
      enableDeferredHydration: false,
    }),
    context,
    fetcherName: 'fetchCMSCatNavData',
  })
  const { id: spyglassID } = getCMSWrapperProps({
    content_type_id: response.content_type_id,
    id: response.id,
  })
  return {
    category: response.data.category_navs.map(toCategory),
    featured_categories: response.data.featured_navs?.map(toCategory) ?? [],
    spyglassID,
  }
}

export const fetchOrderPlacedCMSShelves = (
  context: CMSDataQueryFunctionContext,
  excludeProductIds: number[],
) => {
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'order-placed-page',
    enableDeferredHydration: false,
  })
  return apiPost<LayoutGroupData>({
    fetcherName: 'fetchOrderPlacedCMSShelves',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          exclude_product_ids: excludeProductIds,
        },
      },
    },
    context,
  })
}

export const fetchCartPageCMSShelves = (
  context: CMSDataQueryFunctionContext,
  excludeProductIds: number[],
) => {
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'cart-page',
    enableDeferredHydration: false,
  })
  return apiPost<LayoutGroupData>({
    fetcherName: 'fetchCartPageCMSShelves',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          exclude_product_ids: excludeProductIds,
        },
      },
    },
    context,
  })
}

export const fetchZeroResultsCMSShelves = (
  context: CMSDataQueryFunctionContext<{ query: string }>,
) => {
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'zero-results-page',
    enableDeferredHydration: false,
  })
  return apiPost<LayoutGroupData>({
    fetcherName: 'fetchZeroResultsCMSShelves',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          query: context.queryKey[1].query,
        },
      },
    },
    context,
  })
}

export const fetchAlcoholComplianceTerms = (context: CMSDataQueryFunctionContext) => {
  return apiPost<AlcoholComplianceTerms>({
    fetcherName: 'fetchAlcoholComplianceTerms',
    config: createFetchCMSDataConfig({
      context,
      isOnServer: false,
      contentAlias: 'alcohol-terms-by-state',
      enableDeferredHydration: false,
    }),
    context,
  })
}

export const fetchSearchCMSAdBanner = (
  context: CMSDataQueryFunctionContext<{
    category_id?: number
    query?: string
  }>,
) => {
  const { category_id, query } = context.queryKey[1]
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'search-ad-banner',
    enableDeferredHydration: false,
  })

  return apiPost<LayoutGroupData>({
    fetcherName: 'fetchSearchCMSAdBanner',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          query,
          category_ids: typeof category_id === 'number' ? [category_id] : undefined,
        },
      },
    },
    context,
  })
}

export const fetchSearchCMSAdShowcase = (
  context: CMSDataQueryFunctionContext<{
    category_id?: number
    query?: string
  }>,
) => {
  const { category_id, query } = context.queryKey[1]
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'search-ad-showcase',
    enableDeferredHydration: false,
  })
  return apiPost<AdShowcaseData>({
    fetcherName: 'fetchSearchCMSShowcase',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          query,
          category_ids: typeof category_id === 'number' ? [category_id] : undefined,
        },
      },
    },
    context,
  })
}

export const fetchPdpCMSShelves = (
  context: CMSDataQueryFunctionContext<{ product_id: number }>,
) => {
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'product-description-page',
    enableDeferredHydration: false,
  })
  return apiPost<LayoutGroupData>({
    fetcherName: 'fetchPDPShelvesCMS',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          product_id: context.queryKey[1].product_id,
        },
      },
    },
    context,
  })
}

export const fetchPostCartUpsellShelves = (
  context: CMSDataQueryFunctionContext,
  excludeProductIds: number[],
) => {
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'post-cart-upsell',
    enableDeferredHydration: false,
  })

  return apiPost<LayoutGroupData>({
    fetcherName: 'fetchPostCartUpsellShelves',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          exclude_product_ids: excludeProductIds,
        },
      },
    },
    context,
  })
}

export const fetchCMSGlobalHeaderCategories = (context: CMSDataQueryFunctionContext) => {
  return apiPost<
    Omit<StandardNavData, 'data'> & {
      data: StandardNavData['data'] | undefined
    }
  >({
    fetcherName: 'fetchCMSGlobalHeaderCategories',
    config: createFetchCMSDataConfig({
      context,
      isOnServer: false,
      contentAlias: 'global-header-categories',
      enableDeferredHydration: false,
    }),
    context,
  })
}

export const fetchCMSContentAlias = async <T>(context: CMSDataQueryFunctionContext) => {
  return apiPost<T>({
    config: createFetchCMSDataConfig({
      context,
      isOnServer: false,
      contentAlias: context.queryKey[0].substring(4),
      enableDeferredHydration: false,
    }),
    context,
    fetcherName: 'fetchCMSContentAlias',
  })
}

export const fetchComplementaryShelf = async (
  context: CMSDataQueryFunctionContext<{ product_id: number }>,
) => {
  const [, params] = context.queryKey

  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'search-complementary-shelf',
    enableDeferredHydration: false,
  })

  return apiPost<CMSProductShelfData>({
    fetcherName: 'fetchComplementaryShelf',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          product_id: params.product_id,
        },
      },
    },
    context,
  })
}

export const fetchSearchCMSAdVideoShowcase = (
  context: CMSDataQueryFunctionContext<{ query: string | undefined }>,
) => {
  const [, params] = context.queryKey

  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'search-ad-video-showcase',
    enableDeferredHydration: false,
  })
  return apiPost<AdVideoShowcaseData>({
    fetcherName: 'fetchSearchCMSAdVideoShowcase',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: { query: params.query },
      },
    },
    context,
  })
}

export const fetchCMSPDPAdPlacement = (
  context: CMSDataQueryFunctionContext<{ product_id: number }>,
) => {
  const config = createFetchCMSDataConfig({
    context,
    isOnServer: false,
    contentAlias: 'pdp-ad-placement',
    enableDeferredHydration: false,
  })

  return apiPost<LayoutGroupData>({
    fetcherName: 'fetchCMSPDPAdPlacement',
    config: {
      ...config,
      data: {
        ...config.data,
        input_variables: {
          product_id: context.queryKey[1].product_id,
        },
      },
    },
    context,
  })
}
